import { useCallback } from 'react';

// Helper function to check if a deck folder exists in bundled assets/decks/
const checkDeckFolderExists = (deckName: string): boolean => {
  try {
    // For Capacitor bundled assets, try to create an image element
    // and check if it can load the test file synchronously
    const testImg = new Image();
    testImg.src = `/assets/decks/${deckName}/A13.svg`;
    
    // For bundled assets, if the file exists, the image should load immediately
    // We check if the image is complete and has natural dimensions
    return testImg.complete && testImg.naturalWidth > 0;
  } catch (error) {
    console.warn(`Error checking deck folder existence for "${deckName}":`, error);
    return false;
  }
};

// Extract deck name from API URL - get everything after the last "/"
const extractDeckName = (url: string): string => {
  try {
    // Get everything after the last "/"
    const lastSlashIndex = url.lastIndexOf('/');
    if (lastSlashIndex === -1) return 'orthodoxy'; // fallback if no slash found
    
    const deckName = url.substring(lastSlashIndex + 1);
    if (!deckName) return 'orthodoxy'; // fallback if empty
    
    const normalizedName = deckName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')           // spaces to hyphens
      .replace(/[^a-z0-9-]/g, '')     // remove special chars
      .replace(/-+/g, '-')            // collapse multiple hyphens
      .replace(/(^-)|(-$)/g, '');     // trim edge hyphens
    
    console.log(`Extracted deck name: "${deckName}" -> normalized: "${normalizedName}"`);

    // Check if the deck folder exists in assets/decks/
    if (checkDeckFolderExists(normalizedName)) {
      return normalizedName;
    } else {
      console.log(`Deck folder "${normalizedName}" not found, falling back to "classic"`);
      return 'default';
    }
  } catch (error) {
    console.warn('Error extracting deck name from URL:', url, error);
    return 'default'; // fallback on any error
  }
};

// Dynamic deck folder mapping - no predefined list needed

// Generate standard 52-card deck paths
const generateCardPaths = (folderName: string): Record<string, string> => {
  const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
  const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  
  const cards: Record<string, string> = {};
  
  suits.forEach(suit => {
    ranks.forEach(rank => {
      const cardKey = `${rank}_${suit}`;
      // Based on your naming convention: A2.svg, A3.svg, A4.svg, etc.
      let actualFileName: string;
      
      if (rank === 'A') {
        actualFileName = 'A.svg';
      } else if (rank === 'J') {
        actualFileName = 'A11.svg';
      } else if (rank === 'Q') {
        actualFileName = 'A12.svg';
      } else if (rank === 'K') {
        actualFileName = 'A13.svg';
      } else {
        const rankNum = parseInt(rank);
        actualFileName = `A${rankNum}.svg`;
      }
      
      cards[cardKey] = `/assets/decks/${folderName}/${actualFileName}`;
    });
  });
  
  return cards;
};

export const useLocalDecks = () => {
  const getDeckFromUrl = useCallback((deckUrl: string) => {
    try {
      const extractedName = extractDeckName(deckUrl);
      const folderName = extractedName || 'orthodoxy'; // fallback to orthodoxy
      
      console.log(`Mapping deck URL "${deckUrl}" to folder "${folderName}"`);
      
      return generateCardPaths(folderName);
    } catch (error) {
      console.warn('Error processing deck URL, falling back to orthodoxy:', error);
      return generateCardPaths('orthodoxy');
    }
  }, []);
  
  const getDeckFromName = useCallback((deckName: string) => {
    const normalizedName = deckName.toLowerCase().replace(/\s+/g, '-');
    const folderName = normalizedName || 'orthodoxy';
    return generateCardPaths(folderName);
  }, []);
  
  const getAvailableDecks = useCallback(() => {
    // Since we're fully dynamic, we can't know available decks without filesystem access
    // Return empty array or could be populated from API if needed
    return [];
  }, []);
  
  const isDeckAvailable = useCallback((deckUrl: string) => {
    // Since we're fully dynamic, assume any deck URL can be processed
    // This could be enhanced to check if assets actually exist
    const extractedName = extractDeckName(deckUrl);
    return !!extractedName;
  }, []);
  
  const getCardImagePath = useCallback((deckUrl: string, cardValue: string) => {
    try {
      const extractedName = extractDeckName(deckUrl);
      const folderName = extractedName || 'orthodoxy';
      
      // Handle card back
      if (cardValue === 'back') {
        return `/assets/decks/${folderName}/back.svg`;
      }
      
      // Handle regular cards (A2, A3, ..., A14, B2, ...)
      return `/assets/decks/${folderName}/${cardValue}.svg`;
    } catch (error) {
      console.warn('Error getting card image path, using fallback:', error);
      const fallbackFolder = 'orthodoxy';
      if (cardValue === 'back') {
        return `/assets/decks/${fallbackFolder}/back.svg`;
      }
      return `/assets/decks/${fallbackFolder}/${cardValue}.svg`;
    }
  }, []);

  return {
    getDeckFromUrl,
    getDeckFromName,
    getAvailableDecks,
    isDeckAvailable,
    getCardImagePath,
    extractDeckName // expose for debugging
  };
};


